import { ChangeDetectorRef, Component, Injector, Input, OnChanges, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/takeUntil';
import { PlanfixOffboardingEntry, PlanfixOffboardingLaunch, PlanfixOffboardingRequest } from '../../models/planfix-offboarding.model';
import { WorkflowError } from '../../models/employee-asset.model';
import { OffboardingService } from '../../services/offboarding.service';
import { ASSET } from '../common/employee-assets/asset.constants';
import { assetErrorKey, assetOperationId, safeServiceUrl } from '../common/employee-assets/asset.helpers';
import { OFFBOARDING as O } from './offboarding.constants';

const PROFILE = {locale: 'en-US', separator: '/', dateTemplate: 'dateSelection', datePattern: /^\d{4}-\d{2}-\d{2}$/, year: 2, month: 0, day: 1,
  unavailable: 'OFFBOARD_PLANFIX_UNAVAILABLE', unavailableCode: 'PLANFIX_OFFBOARDING_NOT_CONFIGURED',
  unknown: 'OFFBOARD_PLANFIX_UNKNOWN', rejectedMessage: 'OFFBOARD_PLANFIX_REJECTED', created: 'CREATED', rejected: 'REJECTED',
  dateFormat: {year: 'numeric', month: '2-digit', day: '2-digit'},
  styles: `:host {display:block;width:100%;min-width:0;}
    .offboard-profile-actions {margin:0 0 12px;padding:12px 16px;border:1px solid #dce2e6;border-radius:4px;background:#fff;}
    .offboard-profile-actions .btn {min-height:44px;max-width:100%;white-space:normal;}
    .offboard-profile-row {display:flex;flex-wrap:wrap;align-items:center;gap:12px;}
    .offboard-profile-row label {min-width:0;margin:0;}
    .offboard-profile-row input {flex:0 1 220px;width:auto;min-width:0;min-height:44px;}
    .offboard-profile-row .btn {flex:0 1 auto;min-width:0;margin-left:auto;}
    .offboard-profile-help {margin:8px 0 0;color:#606972;font-size:0.9rem;}
    .offboard-profile-confirm {min-width:0;overflow-wrap:break-word;}
    .offboard-profile-confirm .modal-footer {flex-wrap:wrap;}
    .offboard-profile-confirm .modal-footer .btn {min-height:44px;white-space:normal;}
    @media (max-width:600px) {.offboard-profile-row {gap:8px;} .offboard-profile-row > label, .offboard-profile-row > input, .offboard-profile-row > .btn {flex:1 1 100%;width:100%;}}
  `};

@Component({selector: 'app-offboarding-profile', template: `
  <section class="offboard-profile-actions" *ngIf="loading || errorKey || launch || !compactActions && entry && entry.canStart" [attr.aria-busy]="busy || loading">
    <p *ngIf="loading" role="status">{{ 'ASSET_LOADING' | translate }}</p>
    <p *ngIf="errorKey && !confirming" role="alert">{{ errorKey | translate }}
      <button *ngIf="!busy" class="btn btn-link" type="button" (click)="load()">{{ 'ASSET_CHECK_CURRENT' | translate }}</button></p>
    <ng-container *ngIf="launch">
      <p *ngIf="taskUrl" role="status">{{ 'OFFBOARD_PLANFIX_CREATED' | translate }}
        <a [href]="taskUrl" target="_blank" rel="noopener noreferrer">{{ 'OFFBOARD_PLANFIX_OPEN' | translate }} #{{ launch.taskId }}</a></p>
      <p *ngIf="!taskUrl" role="status">{{ launchMessage | translate }}</p>
      <button *ngIf="!taskUrl" type="button" class="btn btn-link" [disabled]="loading" (click)="load()">{{ 'ASSET_CHECK_CURRENT' | translate }}</button>
    </ng-container>
    <ng-container *ngIf="!compactActions" [ngTemplateOutlet]="terminationForm"></ng-container>
  </section>
  <ng-template #terminationForm>
    <form *ngIf="entry && entry.canStart && !launch" #offboardingForm="ngForm" (ngSubmit)="start(confirmation)">
      <div class="offboard-profile-row">
        <label [attr.for]="'profile-termination-' + employeeId">{{ 'OFFBOARD_TERMINATION_DATE' | translate }}</label>
        <input [id]="'profile-termination-' + employeeId" name="terminationDate" class="form-control" type="date" required [min]="minDate" [(ngModel)]="terminationDate" [readonly]="busy || confirming" [attr.aria-describedby]="'profile-handover-' + employeeId">
      </div>
      <p class="offboard-profile-help" [id]="'profile-handover-' + employeeId">{{ 'OFFBOARD_PLANFIX_DATE_HELP' | translate }}</p>
      <div class="modal-footer px-0">
        <button class="btn btn-danger btn-block" type="submit" [disabled]="busy || confirming || !!errorKey || offboardingForm.invalid || !validDate || !entry.employmentDate">{{ (busy ? 'ASSET_SAVING' : 'OFFBOARD_PROFILE_START') | translate }}</button>
      </div>
    </form>
  </ng-template>
  <ng-template #dateSelection let-dismiss="dismiss">
    <div class="offboard-profile-confirm">
      <div class="modal-header" appWorkflowModalFocus>
        <h4 class="modal-title">{{ 'PROFILE_DISMISS_ACTION' | translate }}</h4>
        <button type="button" class="close" [attr.aria-label]="'CANCEL' | translate" (click)="dismiss()"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="modal-body"><ng-container [ngTemplateOutlet]="terminationForm"></ng-container></div>
    </div>
  </ng-template>
  <ng-template #confirmation let-dismiss="dismiss">
    <div class="offboard-profile-confirm">
      <div class="modal-header" appWorkflowModalFocus>
        <h4 class="modal-title">{{ 'OFFBOARD_PROFILE_CONFIRM_TITLE' | translate }}</h4>
        <button type="button" class="close" [disabled]="busy" [attr.aria-label]="'CANCEL' | translate" (click)="dismiss()"><span aria-hidden="true">×</span></button>
      </div>
      <div class="modal-body" [attr.aria-busy]="busy" *ngIf="confirmationDraft">
        <p><strong>{{ confirmationEmployeeName }}</strong></p>
        <p>{{ 'OFFBOARD_TERMINATION_DATE' | translate }}: <strong>{{ confirmationDraft.terminationDate | date:'dd.MM.yyyy' }}</strong></p>
        <p>{{ 'OFFBOARD_PLANFIX_CONFIRM_HELP' | translate }}</p>
        <div *ngIf="errorKey" class="alert alert-danger" role="alert">{{ errorKey | translate }}
          <button type="button" class="btn btn-link" [disabled]="busy" (click)="checkCurrent()">{{ 'ASSET_CHECK_CURRENT' | translate }}</button>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" [disabled]="busy" (click)="dismiss()">{{ 'CANCEL' | translate }}</button>
        <button type="button" class="btn btn-danger" [disabled]="busy || !!errorKey" (click)="confirmStart()">{{ (busy ? 'ASSET_SAVING' : 'OFFBOARD_PROFILE_CONFIRM_ACTION') | translate }}</button>
      </div>
    </div>
  </ng-template>`, styles: [PROFILE.styles]})
export class OffboardingProfileComponent implements OnChanges, OnDestroy {
  @Input() employeeId: string;
  @Input() compactActions = false;
  @ViewChild(PROFILE.dateTemplate) private dateSelection: TemplateRef<Object>;
  entry: PlanfixOffboardingEntry = null;
  launch: PlanfixOffboardingLaunch = null;
  taskUrl = ASSET.empty;
  launchMessage = ASSET.empty;
  loading = false;
  busy = false;
  confirming = false;
  confirmationDraft: PlanfixOffboardingRequest;
  confirmationEmployeeName = ASSET.empty;
  terminationDate = ASSET.empty;
  minDate = ASSET.empty;
  errorKey = ASSET.empty;
  private request: Subscription;
  private createRequest: Subscription;
  private dialog: NgbModalRef;
  private dateDialog: NgbModalRef;
  private modals: NgbModal;
  private operationId = ASSET.empty;
  private submittedContent = ASSET.empty;
  private disposed = false;
  private timeZone = O.defaultZone;
  private destroyed = new Subject<void>();
  constructor(private api: OffboardingService, private changeDetectorRef: ChangeDetectorRef, injector: Injector) {
    this.modals = injector.get(NgbModal);
  }
  ngOnChanges(): void {
    this.closeDateDialog(); this.closeDialog();
    if (this.request) { this.request.unsubscribe(); }
    if (this.createRequest) { this.createRequest.unsubscribe(); }
    this.entry = null; this.loading = false; this.busy = false; this.errorKey = ASSET.empty;
    this.showLaunch(null);
    this.terminationDate = ASSET.empty; this.operationId = ASSET.empty; this.submittedContent = ASSET.empty;
    this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || O.defaultZone;
    if (this.employeeId) { this.load(); }
  }
  ngOnDestroy(): void {
    this.disposed = true; this.closeDateDialog(); this.closeDialog(); this.destroyed.next(); this.destroyed.complete();
  }
  get validDate(): boolean {
    return PROFILE.datePattern.test(this.terminationDate) && this.terminationDate >= this.minDate;
  }
  load(): void {
    if (this.disposed || this.busy || this.confirming || !this.employeeId) { return; }
    if (this.request) { this.request.unsubscribe(); }
    this.loading = true; this.errorKey = ASSET.empty; this.entry = null; this.showLaunch(null);
    const employee = this.employeeId;
    this.request = this.api.planfixEntry(employee).takeUntil(this.destroyed).subscribe(result => {
      if (!this.current(employee)) { return; }
      this.entry = result; this.loading = false;
      this.showLaunch(result.launch);
      if (!result.launch) { this.operationId = ASSET.empty; }
      const today = this.today();
      this.minDate = result.employmentDate && result.employmentDate > today ? result.employmentDate : today;
      if (result.canStart && !result.employmentDate) { this.errorKey = O.employmentMissing; }
      this.changeDetectorRef.markForCheck();
    }, error => {
      if (!this.current(employee)) { return; }
      this.loading = false; this.errorKey = error && error.status === ASSET.forbidden ? ASSET.empty : assetErrorKey(error, false); this.changeDetectorRef.markForCheck();
    });
    this.changeDetectorRef.markForCheck();
  }
  openDateSelection(): void {
    if (this.disposed || this.dateDialog || this.dialog || this.busy || this.loading || this.errorKey ||
      !this.entry || !this.entry.canStart || this.launch || !this.entry.employmentDate) { return; }
    const ref = this.modals.open(this.dateSelection);
    this.dateDialog = ref;
    const closed = () => { if (this.dateDialog === ref) { this.dateDialog = null; } };
    ref.result.then(closed, closed);
  }
  start(content: TemplateRef<Object>): void {
    if (!this.canStart() || this.dialog) { return; }
    this.closeDateDialog();
    this.confirmationDraft = this.prepare();
    this.confirmationEmployeeName = this.entry.employeeName || this.employeeId;
    const ref = this.modals.open(content, {beforeDismiss: () => !this.busy});
    this.dialog = ref; this.confirming = true;
    ref.result.then(() => this.closed(ref), () => this.closed(ref));
    this.changeDetectorRef.markForCheck();
  }
  confirmStart(): void {
    if (!this.dialog || !this.confirmationDraft || !this.canStart()) { return; }
    const draft = this.confirmationDraft;
    if (draft.employeeId !== this.employeeId || draft.employmentDate !== this.entry.employmentDate || draft.terminationDate !== this.terminationDate) { this.closeDialog(); return; }
    if (!this.entry.integrationReady) { this.errorKey = PROFILE.unavailable; this.changeDetectorRef.markForCheck(); return; }
    const employee = this.employeeId;
    this.busy = true; this.errorKey = ASSET.empty;
    this.createRequest = this.api.launchPlanfix(draft).takeUntil(this.destroyed).subscribe(result => {
      if (!this.current(employee)) { return; }
      this.showLaunch(result); this.busy = false; this.closeDialog(); this.changeDetectorRef.markForCheck();
    }, error => {
      if (!this.current(employee)) { return; }
      this.busy = false; this.errorKey = this.launchError(error); this.changeDetectorRef.markForCheck();
    });
  }
  checkCurrent(): void {
    if (this.busy || this.disposed) { return; }
    this.closeDialog(); this.load();
  }
  private canStart(): boolean {
    return !this.disposed && !this.busy && !this.loading && !this.errorKey && !!this.entry && this.entry.canStart &&
      !this.launch && this.validDate && !!this.entry.employmentDate;
  }
  private showLaunch(result: PlanfixOffboardingLaunch): void {
    this.launch = result || null;
    this.taskUrl = result && result.status === PROFILE.created ? safeServiceUrl(result.taskUrl) : ASSET.empty;
    this.launchMessage = result && result.status === PROFILE.rejected ? PROFILE.rejectedMessage : PROFILE.unknown;
  }
  private launchError(error: WorkflowError): string {
    if (error && error.error && error.error.code === PROFILE.unavailableCode) { return PROFILE.unavailable; }
    return assetErrorKey(error, true);
  }
  private closed(ref: NgbModalRef): void {
    if (this.dialog !== ref) { return; }
    this.dialog = null; this.confirming = false; this.confirmationDraft = null;
    if (!this.disposed) { this.changeDetectorRef.markForCheck(); }
  }
  private closeDialog(): void {
    if (!this.dialog) { return; }
    const ref = this.dialog;
    this.closed(ref); ref.close();
  }
  private closeDateDialog(): void {
    if (!this.dateDialog) { return; }
    const ref = this.dateDialog; this.dateDialog = null; ref.close();
  }
  private prepare(): PlanfixOffboardingRequest {
    const content = JSON.stringify([this.employeeId, this.entry.employmentDate, this.terminationDate, this.timeZone]);
    if (!this.operationId || this.submittedContent !== content) { this.operationId = assetOperationId(); }
    this.submittedContent = content;
    return {operationId: this.operationId, employeeId: this.employeeId, employmentDate: this.entry.employmentDate,
      terminationDate: this.terminationDate, timeZone: this.timeZone};
  }
  private today(): string {
    const options = Object.assign({}, PROFILE.dateFormat, {timeZone: this.timeZone});
    const parts = new Intl.DateTimeFormat(PROFILE.locale, options).format(new Date()).split(PROFILE.separator);
    return [parts[PROFILE.year], parts[PROFILE.month], parts[PROFILE.day]].join(ASSET.dash);
  }
  private current(employee: string): boolean {
    return !this.disposed && this.employeeId === employee;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-profile.component.ts